/**
 * Floating Action Button Component
 *
 * Renders a floating button that opens the PSC submission dialog.
 */

import { useEffect } from 'react';
import { usePSCContext } from '../hooks/usePSCContext';
import clsx from 'clsx';

// Plus icon SVG
const PlusIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

export function FloatingButton() {
  const { config, open, isOpen } = usePSCContext();

  // Handle keyboard shortcut
  useEffect(() => {
    if (!config.keyboardShortcut) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      const shortcut = config.keyboardShortcut.toLowerCase();
      const keys = shortcut.split('+');

      const ctrlRequired = keys.includes('ctrl');
      const shiftRequired = keys.includes('shift');
      const altRequired = keys.includes('alt');
      // metaRequired for future macOS support
      void (keys.includes('meta') || keys.includes('cmd'));
      const key = keys.find(
        (k) => !['ctrl', 'shift', 'alt', 'meta', 'cmd'].includes(k)
      );

      if (
        (ctrlRequired === event.ctrlKey || ctrlRequired === event.metaKey) &&
        shiftRequired === event.shiftKey &&
        altRequired === event.altKey &&
        key &&
        event.key.toLowerCase() === key
      ) {
        event.preventDefault();
        open();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [config.keyboardShortcut, open]);

  if (!config.enabled) return null;

  // Position classes
  const positionClasses = {
    'bottom-right': 'psc-button--bottom-right',
    'bottom-left': 'psc-button--bottom-left',
    'top-right': 'psc-button--top-right',
    'top-left': 'psc-button--top-left',
  };

  return (
    <button
      onClick={open}
      disabled={isOpen}
      aria-label="Open feedback dialog"
      data-testid="psc-floating-button"
      className={clsx(
        'psc-floating-button',
        positionClasses[config.position],
        config.buttonClassName,
        isOpen && 'psc-button--hidden'
      )}
      style={{ zIndex: config.zIndex }}
      title={`Submit feedback (${config.keyboardShortcut})`}
    >
      <PlusIcon />
    </button>
  );
}
